import { useCallback, useEffect, useState } from "react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

// Bandeau de consentement cookies conforme CNIL :
// Accepter / Refuser / Personnaliser, égale visibilité, aucune case pré-cochée.
const STORAGE_KEY = "astrolog-consent";

const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const [customizing, setCustomizing] = useState(false);
  const [analytics, setAnalytics] = useState(false);

  useEffect(() => {
    try {
      if (!localStorage.getItem(STORAGE_KEY)) setVisible(true);
    } catch {
      setVisible(true);
    }
    const reopen = () => {
      setVisible(true);
      setCustomizing(true);
    };
    window.addEventListener("astrolog:open-cookies", reopen);
    return () => window.removeEventListener("astrolog:open-cookies", reopen);
  }, []);

  const save = useCallback((analyticsConsent) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          necessary: true,
          analytics: analyticsConsent,
          date: new Date().toISOString(),
        })
      );
    } catch {
      /* stockage indisponible : le choix vaut pour la session */
    }
    setVisible(false);
    setCustomizing(false);
  }, []);

  if (!visible) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-50 p-4 sm:p-6"
      role="dialog"
      aria-label="Consentement aux cookies"
      data-testid="cookie-banner"
    >
      <div className="mx-auto max-w-3xl rounded-2xl border border-slate-200 bg-white p-6 shadow-[0_20px_50px_rgba(15,23,42,0.25)]">
        <h2 className="font-heading text-lg font-semibold">Respect de votre vie privée</h2>
        <p className="mt-2 text-sm leading-relaxed text-slate-600">
          Nous utilisons des cookies strictement nécessaires au fonctionnement du site
          et, avec votre accord, des cookies de mesure d'audience. Vous pouvez accepter,
          refuser ou personnaliser votre choix. Consultez notre{" "}
          <a href="/politique-de-confidentialite" className="font-medium text-primary underline">
            politique de confidentialité
          </a>
          .
        </p>

        {customizing && (
          <div className="mt-4 space-y-3 rounded-xl bg-slate-50 p-4" data-testid="cookie-customize-panel">
            <div className="flex items-center justify-between gap-4">
              <Label className="text-sm font-medium">Cookies strictement nécessaires</Label>
              <span className="text-xs font-semibold uppercase text-slate-500">Toujours actifs</span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <Label htmlFor="cookie-analytics" className="text-sm font-medium">
                Mesure d'audience (anonymisée)
              </Label>
              <Switch
                id="cookie-analytics"
                data-testid="cookie-analytics-switch"
                checked={analytics}
                onCheckedChange={setAnalytics}
              />
            </div>
          </div>
        )}

        <div className="mt-5 flex flex-col gap-3 sm:flex-row">
          <button
            type="button"
            data-testid="cookie-accept-button"
            onClick={() => save(true)}
            className="flex-1 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-white transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-[#1D4ED8]"
          >
            Tout accepter
          </button>
          <button
            type="button"
            data-testid="cookie-refuse-button"
            onClick={() => save(false)}
            className="flex-1 rounded-full border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-slate-100"
          >
            Tout refuser
          </button>
          {customizing ? (
            <button
              type="button"
              data-testid="cookie-save-button"
              onClick={() => save(analytics)}
              className="flex-1 rounded-full border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-slate-100"
            >
              Enregistrer mes choix
            </button>
          ) : (
            <button
              type="button"
              data-testid="cookie-customize-button"
              onClick={() => setCustomizing(true)}
              className="flex-1 rounded-full border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-slate-100"
            >
              Personnaliser
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
